import { cloneDeep } from '../libs/cloneDeep';
import uuid from './utils/uuid';
import { getPreffix, getSuffix } from './utils';
import type { OpaqueAsset, RawAssetData } from './types';

/**
 * Returns an ordered list of duplicated OpaqueAssets, where the  OpaqueVideoData[0] === topParent
 *
 * This function supports a flat tree of unordered OpaqueAssets.
 * It makes sure to duplicate every child asset and generate new "id" and name.
 * It rebuilds the relationship with the new "ids". It works like a clone.
 *
 * Note: This function supports name collision prevention.
 */


function duplicateAssetData(opaqueAssets: OpaqueAsset[] |RawAssetData[], blackListNames: string[] = []): OpaqueAsset[] {
    const rawAssets = opaqueAssets as RawAssetData[];


    if (!rawAssets.length) {
        return [];
    }

    // initializing the hash memory by holding the original top parentId
    // this way we can receive an unsorted list of OpaqueAssets[] and it will duplicate
    // every element correctly and maintain the correct old and new keys
    // MapStructure { [originalId]: {newId} } PS: The id's are generated with "uuid()"
    // e.g: { "b76b7581-6a08-9aee-a75d-66c47f32d1c6": ""b76b7581-6a08-9aee-a75d-66c47f32d1f3"" }
    const assetOriginalParentIds = {};


    const rawAssetNames = rawAssets.map(asset => asset.name);
    const uniqueNames = new Set([...rawAssetNames, ...blackListNames ]);

    // Track the highestSuffix of each element  so we prevent name collision.
    // MapStructure { [classType]: {highestSuffix} }
    // e.g: { 'Text': 1, "Group": 2 }
    const highestSuffixByName = Array.from(uniqueNames).reduce((acc, name) => {
        const preffix = getPreffix(name);
        const suffixFound = acc[preffix];
        const currentSuffix = getSuffix(name);

        if (!suffixFound || currentSuffix > suffixFound) {
            acc[preffix] = currentSuffix;
        }

        return acc;
    }, {});

    const final = [];

    for (let i = 0; i < rawAssets.length; i++) {
        const currentAsset = rawAssets[i];
        const parentId = assetOriginalParentIds[currentAsset.parentId];
        const hasNoParent = !currentAsset.parentId;


        const currentPreffix = getPreffix(currentAsset.name);
        let highestSuffix = highestSuffixByName[currentPreffix];
        const newName = `${currentPreffix}_${++highestSuffix}`;
        const newId = uuid();
        const clone = cloneDeep(currentAsset);

        if (rawAssets.length === 1 || hasNoParent) {
            final.unshift({
                ...clone,
                id: newId,
                name: newName,
                parentId: null,
            });
        }
        else {
            final.push({
                ...clone,
                id: newId,
                parentId,
                name: newName,
            });
        }


        highestSuffixByName[currentPreffix] = highestSuffix;
        assetOriginalParentIds[currentAsset.id] = newId;
    }


    return final;
}


export default duplicateAssetData;
